import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function ContactPage() {
  usePageTitle("お問い合わせ");
  const [submitted, setSubmitted] = useState(false);
  const [copied, setCopied] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCopyEmail = () => {
    navigator.clipboard.writeText("r.fujimoto@jpd.ne.jp").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const data = new URLSearchParams();
    data.append("name", formData.name);
    data.append("company", formData.company);
    data.append("email", formData.email);
    data.append("phone", formData.phone);
    data.append("subject", formData.subject);
    data.append("message", formData.message);
    fetch(form.action, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: data.toString(),
    }).finally(() => setSubmitted(true));
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="pt-24 md:pt-32 pb-12 md:pb-16">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-2xl mx-auto text-center py-16 md:py-24">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-ocean-50 text-ocean-500">
                <i className="ri-check-line text-4xl" />
              </div>
              <h2 className="font-serif text-2xl md:text-3xl font-bold text-ocean-800 mb-4">
                お問い合わせありがとうございます
              </h2>
              <p className="text-ocean-600 text-sm md:text-base mb-2">
                内容を確認の上、通常1営業日以内にご返信いたします。
              </p>
              <p className="text-ocean-500 text-sm">
                しばらくお待ちください。
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-ocean-800">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="font-serif text-3xl md:text-5xl font-bold text-white mb-4">
                  お問い合わせ
                </h1>
                <p className="text-white/70 max-w-xl mx-auto text-sm md:text-base">
                  お気軽にお問い合わせください。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 md:py-20 bg-ocean-50">
          <div className="px-4 md:px-8 lg:px-12">
            <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <div className="bg-ocean-800 rounded-2xl p-6 md:p-8 text-white mb-6">
                  <h2 className="font-serif text-xl font-bold mb-6">
                    連絡先情報
                  </h2>
                  <div className="space-y-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-white/10">
                        <i className="ri-map-pin-line text-ocean-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1">所在地</p>
                        <p className="text-sm text-white/70">
                          〒802-0032
                          <br />
                          福岡県北九州市小倉北区赤坂3丁目6-22-1F
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-white/10">
                        <i className="ri-phone-line text-ocean-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1">電話</p>
                        <a
                          href="tel:0932872050"
                          className="text-sm text-ocean-300 hover:text-white transition-colors underline underline-offset-2 cursor-pointer"
                        >
                          093-287-2050
                        </a>
                        <p className="text-xs text-white/50 mt-1">
                          平日 9:00〜18:00
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-white/10">
                        <i className="ri-mail-line text-ocean-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1">メール</p>
                        <button
                          type="button"
                          onClick={handleCopyEmail}
                          className="flex items-center gap-1.5 text-sm text-ocean-300 hover:text-white transition-colors cursor-pointer group"
                        >
                          <span className="underline underline-offset-2">r.fujimoto@jpd.ne.jp</span>
                          <i className={`text-xs ${copied ? "ri-check-line text-green-400" : "ri-file-copy-line"}`} />
                        </button>
                        {copied && (
                          <p className="text-xs text-green-400 mt-1">コピーしました！</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-lg bg-white/10">
                        <i className="ri-time-line text-ocean-300" />
                      </div>
                      <div>
                        <p className="text-sm font-semibold mb-1">営業時間</p>
                        <p className="text-sm text-white/70">
                          平日 9:00〜18:00
                          <br />
                          土日祝日休業
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps?q=%E5%B0%8F%E5%80%89%E5%8C%97%E5%8C%BA%E8%B5%A4%E5%9D%823%E4%B8%81%E7%9B%AE6-22&output=embed"
                    width="100%"
                    height="250"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="会社所在地"
                  />
                </div>
              </div>

              <div className="lg:col-span-3">
                <div className="bg-white rounded-2xl p-6 md:p-8 border border-ocean-100">
                  <h2 className="font-serif text-xl font-bold text-ocean-800 mb-6">
                    お問い合わせフォーム
                  </h2>

                  <form
                    id="contact-form"
                    action="https://readdy.ai/api/form/d7lmk1lnkhlbktri2pcg"
                    method="POST"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label
                          htmlFor="name"
                          className="block text-sm font-medium text-ocean-700 mb-1.5"
                        >
                          お名前 <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-ocean-200 text-sm text-ocean-800 placeholder:text-ocean-300 focus:outline-none focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500 transition-colors"
                          placeholder="山田 太郎"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="company"
                          className="block text-sm font-medium text-ocean-700 mb-1.5"
                        >
                          会社名
                        </label>
                        <input
                          type="text"
                          id="company"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-ocean-200 text-sm text-ocean-800 placeholder:text-ocean-300 focus:outline-none focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500 transition-colors"
                          placeholder="株式会社○○"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label
                          htmlFor="email"
                          className="block text-sm font-medium text-ocean-700 mb-1.5"
                        >
                          返信先メールアドレス{" "}
                          <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-ocean-200 text-sm text-ocean-800 placeholder:text-ocean-300 focus:outline-none focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500 transition-colors"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="phone"
                          className="block text-sm font-medium text-ocean-700 mb-1.5"
                        >
                          電話番号
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 rounded-lg border border-ocean-200 text-sm text-ocean-800 placeholder:text-ocean-300 focus:outline-none focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500 transition-colors"
                          placeholder="03-XXXX-XXXX"
                        />
                      </div>
                    </div>

                    <div>
                      <label
                        htmlFor="subject"
                        className="block text-sm font-medium text-ocean-700 mb-1.5"
                      >
                        お問い合わせ項目{" "}
                        <span className="text-red-400">*</span>
                      </label>
                      <select
                        id="subject"
                        name="subject"
                        required
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-ocean-200 text-sm text-ocean-800 focus:outline-none focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500 transition-colors bg-white"
                      >
                        <option value="">選択してください</option>
                        <option value="estimate">お見積もり依頼について</option>
                        <option value="recruit">求人募集について</option>
                        <option value="career">採用について</option>
                        <option value="other">その他</option>
                      </select>
                    </div>

                    <div>
                      <label
                        htmlFor="message"
                        className="block text-sm font-medium text-ocean-700 mb-1.5"
                      >
                        お問い合わせ内容{" "}
                        <span className="text-red-400">*</span>
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        rows={6}
                        maxLength={500}
                        value={formData.message}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-lg border border-ocean-200 text-sm text-ocean-800 placeholder:text-ocean-300 focus:outline-none focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500 transition-colors resize-none"
                        placeholder="お問い合わせ内容をご記入ください（500文字以内）"
                      />
                      <p className="text-xs text-ocean-400 mt-1 text-right">
                        {formData.message.length}/500文字
                      </p>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-10 py-4 bg-ocean-500 hover:bg-ocean-400 text-white font-semibold rounded-full transition-all whitespace-nowrap"
                      >
                        送信する
                        <i className="ri-send-plane-line" />
                      </button>
                      <p className="text-xs text-ocean-400 mt-3">
                        通常1営業日以内にご返信いたします。
                      </p>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}